import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ApiConnectionsModel, WorkspaceRepository } from '@database/central';
import { DataRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { TranslateService } from '@ngx-translate/core';
import { CardButtonsStatusEnum, DataModel, ErrorCodes, ToastDuration, ToastService, YesNoEnum } from '@shared';
import { Observable, Subject, first, firstValueFrom, map, of, switchMap, takeUntil } from 'rxjs';
import { ConnectionTypeEnum } from 'src/domain/connections/models/connection-type.enum';
import { ConnectionsManagerService } from 'src/domain/connections/services/connections-manager.service';
import { TransferOutStateService } from '../connection-options';
import {
	CopyinCheckinCheckoutService,
	CopyinCheckinCheckoutModel,
	BusinessLayerService,
	IModal,
	CheckOutResponseDto,
	CheckoutService
} from '@domain/business-layer';
import { LoggingService } from '@shared/logging';
import { ApiErrorModel } from '@api/http';
import { RequestsStatusState } from '@api/external-transfer';
import { ModalController, IonRouterOutlet } from '@ionic/angular';
import { EntryPreviewComponent } from 'src/pages/entry-details/components/entry-preview/entry-preview.component';


enum ModalResponseType {
	connectionModelHasOptions = 'connectionModelHasOptions',
	modalClosedWithNoResponse = 'modalClosedWithNoResponse',
	modalClosedWithNewConnection = 'modalClosedWithNewConnection'
}

interface ConnectionManagerModalResponse {
	modal?: IModal;
	responseType: ModalResponseType;
}

@Component({
	selector: 'app-copyin-checkin-checkout',
	templateUrl: './copyin-checkin-checkout.component.html',
	styleUrls: ['./copyin-checkin-checkout.component.scss'],
	providers: [TransferOutStateService]
})
export class CopyinCheckinCheckoutComponent implements OnInit, OnDestroy {
	@Input() public entityModel: CopyinCheckinCheckoutModel;
	@Input() public isNoApiModeEnabled: boolean;

	public buttonsStatus = CardButtonsStatusEnum.masterRecord;
	public cardButtonsStatus = CardButtonsStatusEnum;
	public isRequestPending = true;
	private unsubscriber$: Subject<boolean> = new Subject<boolean>();

	public constructor(
		private dataRepository: DataRepository,
		private toastService: ToastService,
		private workspaceRepository: WorkspaceRepository,
		private translateService: TranslateService,
		private connectionsManager: ConnectionsManagerService,
		private copyInCheckInCheckOutService: CopyinCheckinCheckoutService,
		private businessLayerService: BusinessLayerService,
		private loggingService: LoggingService,
		private checkoutService: CheckoutService,
		private modalCtrl: ModalController,
		private routerOutlet: IonRouterOutlet,
		private requestsStatusState: RequestsStatusState
	) {}

	private get connectionModel$(): Observable<ConnectionManagerModalResponse> {
		return this.businessLayerService.getConnectionModel$(this.entityModel.workfileId).pipe(
			switchMap((apiConnectionModel?: ApiConnectionsModel) => {
				if (!apiConnectionModel?.selectedOptions) {
					return this.connectionsManager.open(ConnectionTypeEnum.includeOptions).pipe(
						map(
							(modalResponse: IModal) =>
								({
									modal: modalResponse,
									responseType: modalResponse
										? ModalResponseType.modalClosedWithNewConnection
										: ModalResponseType.modalClosedWithNoResponse
								}) as ConnectionManagerModalResponse
						)
					);
				} else {
					return of({
						modal: null,
						responseType: ModalResponseType.connectionModelHasOptions
					} as ConnectionManagerModalResponse);
				}
			}),
			first()
		);
	}

	private get flattenPrimaryKeysAndValues$(): Observable<string> {
		return this.businessLayerService.flattenPrimaryKeysAndValues$(
			this.entityModel.workfileId,
			this.entityModel.projectNumber,
			this.entityModel.entityName,
			this.entityModel.id
		);
	}

	public ngOnInit(): void {
		this.dataRepository
			.getAllTableData$(this.entityModel.entityName)
			.pipe(takeUntil(this.unsubscriber$), first())
			.subscribe((tableData: DataModel[]) => {
				const data: DataModel = tableData.find((d) => d.rxdbid === this.entityModel.id);
				if (data) {
					this.buttonsStatus =
						data.is_master === YesNoEnum.Yes
							? CardButtonsStatusEnum.masterRecord
							: CardButtonsStatusEnum.copyRecord;
				}
			});

		const requestStatus$ = this.requestsStatusState.get$(this.entityModel.entityName, this.entityModel.id);
		if (!requestStatus$) {
			this.isRequestPending = false;
			return;
		}

		requestStatus$.pipe(takeUntil(this.unsubscriber$)).subscribe(
			() => {
				this.isRequestPending = true;
			},
			() => (this.isRequestPending = false),
			() => (this.isRequestPending = false)
		);
	}

	public checkIn() {
		if (this.isNoApiModeEnabled) {
			this.toastService.presentError(
				this.translateService.instant('noApiMode.messages.error'),
				ToastDuration.Fast
			);
			return;
		}

		this.connectionModel$
			.pipe(
				switchMap((connectionManagerModalResponse: ConnectionManagerModalResponse) => {
					switch (connectionManagerModalResponse.responseType) {
						case ModalResponseType.connectionModelHasOptions:
							this.isRequestPending = true;
							return this.copyInCheckInCheckOutService.checkIn$(this.entityModel);
						case ModalResponseType.modalClosedWithNewConnection:
							this.workspaceRepository.setWorkfileConnectionId(
								this.entityModel.workfileId,
								connectionManagerModalResponse.modal.data
							);
							return of(null);
						case ModalResponseType.modalClosedWithNoResponse:
							return of(null);
					}
				})
			)
			.subscribe(
				async (result) => {
					if (!result) {
						return;
					}

					this.isRequestPending = false;
					this.buttonsStatus = this.cardButtonsStatus.copyRecord;
					const flattenPrimaryKeysAndValues = await firstValueFrom(this.flattenPrimaryKeysAndValues$);

					await this.dataRepository.updateOne(
						{ [WorkspaceDatabaseKeys.columns.isMaster]: YesNoEnum.No },
						this.entityModel.entityName,
						this.entityModel.id,
						[]
					);

					await this.toastService.presentSuccess(
						`Record: ${flattenPrimaryKeysAndValues}<br />${this.translateService.instant(
							'cards.success.checkIn'
						)}`,
						ToastDuration.Fast
					);
				},
				async (error: ApiErrorModel) => {
					this.loggingService.error(ErrorCodes.CheckInError, error.errorMessage);
					this.isRequestPending = false;
					const flattenPrimaryKeysAndValues = await firstValueFrom(this.flattenPrimaryKeysAndValues$);

					await this.toastService.presentError(
						`Record: ${flattenPrimaryKeysAndValues}<br /> Message: ${error.errorMessage}`,
						ToastDuration.Slow
					);
				}
			);
	}

	public copyIn() {
		if (this.isNoApiModeEnabled) {
			this.toastService.presentError(
				this.translateService.instant('noApiMode.messages.error'),
				ToastDuration.Fast
			);
			return;
		}

		this.connectionModel$
			.pipe(
				switchMap((connectionManagerModalResponse: ConnectionManagerModalResponse) => {
					switch (connectionManagerModalResponse.responseType) {
						case ModalResponseType.connectionModelHasOptions:
							this.isRequestPending = true;
							return this.copyInCheckInCheckOutService.copyIn$(this.entityModel);
						case ModalResponseType.modalClosedWithNewConnection:
							this.workspaceRepository.setWorkfileConnectionId(
								this.entityModel.workfileId,
								connectionManagerModalResponse.modal.data
							);
							return of(null);
						case ModalResponseType.modalClosedWithNoResponse:
							return of(null);
					}
				})
			)
			.subscribe(
				async (result) => {
					if (!result) {
						return;
					}

					this.isRequestPending = false;
					this.buttonsStatus = this.cardButtonsStatus.masterRecord;
					const flattenPrimaryKeysAndValues = await firstValueFrom(this.flattenPrimaryKeysAndValues$);

					await this.toastService.presentSuccess(
						`Record: ${flattenPrimaryKeysAndValues}<br />${this.translateService.instant(
							'cards.success.copyIn'
						)}`,
						ToastDuration.Fast
					);
				},
				async (error: ApiErrorModel) => {
					this.loggingService.error(ErrorCodes.CopyInError, error.errorMessage);
					this.isRequestPending = false;
					const flattenPrimaryKeysAndValues = await firstValueFrom(this.flattenPrimaryKeysAndValues$);

					await this.toastService.presentError(
						`Record: ${flattenPrimaryKeysAndValues}<br />${error.errorMessage}`,
						ToastDuration.Slow
					);
				}
			);
	}

	public checkOut() {
		if (this.isNoApiModeEnabled) {
			this.toastService.presentError(
				this.translateService.instant('noApiMode.messages.error'),
				ToastDuration.Fast
			);
			return;
		}

		this.connectionModel$
			.pipe(
				switchMap((connectionManagerModalResponse: ConnectionManagerModalResponse) => {
					switch (connectionManagerModalResponse.responseType) {
						case ModalResponseType.connectionModelHasOptions:
							this.isRequestPending = true;
							return this.copyInCheckInCheckOutService.checkOut$(this.entityModel);
						case ModalResponseType.modalClosedWithNewConnection:
							this.workspaceRepository.setWorkfileConnectionId(
								this.entityModel.workfileId,
								connectionManagerModalResponse.modal.data
							);
							return of(null);
						case ModalResponseType.modalClosedWithNoResponse:
							return of(null);
					}
				})
			)
			.subscribe(
				async (result: CheckOutResponseDto | null) => {
					if (!result) {
						return;
					}

					this.isRequestPending = false;
					let tableData: DataModel[] = [];
					const flattenPrimaryKeysAndValues = await firstValueFrom(this.flattenPrimaryKeysAndValues$);

					try {
						tableData = JSON.parse(result.Workfile).TABLE_DATA;
					} catch (error) {
						this.loggingService.error(ErrorCodes.CheckOutJsonParseError, error);
						await this.toastService.presentError(error, ToastDuration.Slow);
					}

					try {
						await this.checkoutService.updateRecords(
							this.entityModel.workfileId,
							this.entityModel.entityName,
							this.entityModel.id,
							tableData
						);

						this.buttonsStatus = this.cardButtonsStatus.masterRecord;

						await this.toastService.presentSuccess(
							`Record: ${flattenPrimaryKeysAndValues}<br />${this.translateService.instant(
								'cards.success.checkOut'
							)}`,
							ToastDuration.Fast
						);
					} catch (error) {
						this.loggingService.error(ErrorCodes.CheckoutError, error);

						await this.toastService.presentError(
							`Record: ${flattenPrimaryKeysAndValues}<br />
						${this.translateService.instant(error.errorMessage)}`,
							ToastDuration.Slow
						);
					}
				},
				async (error: ApiErrorModel) => {
					this.loggingService.error(ErrorCodes.CheckoutError, error.errorMessage);
					this.isRequestPending = false;
					const flattenPrimaryKeysAndValues = await firstValueFrom(this.flattenPrimaryKeysAndValues$);

					await this.toastService.presentError(
						`Record: ${flattenPrimaryKeysAndValues}<br />
					${this.translateService.instant(error.errorMessage)}`,
						ToastDuration.Slow
					);
				}
			);
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next(true);
		this.unsubscriber$.unsubscribe();
	}

	public async viewReportPreview() : Promise<void> {
		const modal = await this.modalCtrl.create({
			component: EntryPreviewComponent,
			cssClass: 'entry-preview-modal', 
			presentingElement: this.routerOutlet.nativeEl,
			componentProps: {
				workfileId: this.entityModel.workfileId, 
				entityName: this.entityModel.entityName, 
				entryId: this.entityModel.id, 
				projectNumber: this.entityModel.projectNumber 
			}
			
		});
	
		await modal.present();
	
		const { data } = await modal.onDidDismiss();
	}
}
